import React, { useEffect, useMemo, useState } from "react";
import {
  Boxes,
  Check,
  ClipboardList,
  PackageSearch,
  Settings,
  Tags,
} from "lucide-react";
import {
  CLIENT_PRICING_TIER_OPTIONS,
  getPricingTierLabel,
} from "../utils/pricingTemplates";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import FormInput from "./FormInput";
import "./WorkflowLauncher.css";

const QUOTE_ACTIONS = CLIENT_PRICING_TIER_OPTIONS.map((tier) => ({
  type: "quote",
  tier: tier.value,
  label: `${tier.label} Quote`,
}));

const ADMIN_ACTIONS = [
  {
    type: "route",
    path: "/admin/materials",
    label: "Materials",
    icon: PackageSearch,
  },
  {
    type: "route",
    path: "/admin/rate-cards",
    label: "Rate Cards",
    icon: Boxes,
  },
  {
    type: "route",
    path: "/admin/pricing-settings",
    label: "Pricing Tiers",
    icon: Settings,
  },
];

const buildInitialDraft = (tier) => ({
  job_name: "",
  customer_name: "",
  client_pricing_tier: tier,
});

function WorkflowLauncher({
  open,
  isAdmin,
  isSaving,
  error,
  defaultTier,
  onClose,
  onCreateEstimate,
  onNavigate,
}) {
  const resolvedDefaultTier = defaultTier || "retail";
  const [selectedTier, setSelectedTier] = useState(resolvedDefaultTier);
  const [draft, setDraft] = useState(buildInitialDraft(resolvedDefaultTier));

  useEffect(() => {
    if (open) {
      setSelectedTier(resolvedDefaultTier);
      setDraft(buildInitialDraft(resolvedDefaultTier));
    }
  }, [open, resolvedDefaultTier]);

  const quoteActions = useMemo(
    () =>
      QUOTE_ACTIONS.map((action) => ({
        ...action,
        active: action.tier === selectedTier,
      })),
    [selectedTier],
  );

  const setDraftField = (field, value) => {
    setDraft((previous) => ({ ...previous, [field]: value }));
  };

  const selectTier = (tier) => {
    setSelectedTier(tier);
    setDraft((previous) => ({
      ...previous,
      client_pricing_tier: tier,
    }));
  };

  const handleCreate = async () => {
    await onCreateEstimate({
      ...draft,
      client_pricing_tier: draft.client_pricing_tier || selectedTier,
    });
  };

  const canCreate = Boolean(draft.job_name.trim()) && !isSaving;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="workflow-launcher">
        <DialogHeader className="workflow-launcher__header">
          <DialogTitle>Start CostPilot</DialogTitle>
          <DialogDescription>
            Choose the work, capture the job shell, then build the estimate.
          </DialogDescription>
        </DialogHeader>

        <div className="workflow-launcher__body">
          {isAdmin && (
            <section className="workflow-launcher__section">
              <div className="workflow-launcher__section-title">
                <Settings size={16} />
                <span>Admin work</span>
              </div>
              <div className="workflow-launcher__admin-grid">
                {ADMIN_ACTIONS.map((action) => {
                  const Icon = action.icon;
                  return (
                    <button
                      key={action.path}
                      type="button"
                      className="wb-plain workflow-launcher__admin-btn"
                      onClick={() => onNavigate(action.path)}
                    >
                      <Icon size={16} aria-hidden="true" />
                      <span>{action.label}</span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section className="workflow-launcher__section">
            <div className="workflow-launcher__section-title">
              <ClipboardList size={16} />
              <span>Start quote</span>
            </div>
            <div className="workflow-launcher__quote-grid">
              {quoteActions.map((action) => (
                <button
                  key={action.tier}
                  type="button"
                  className={`wb-plain workflow-launcher__quote-btn${
                    action.active ? " workflow-launcher__quote-btn--active" : ""
                  }`}
                  aria-pressed={action.active}
                  onClick={() => selectTier(action.tier)}
                >
                  <Tags size={16} aria-hidden="true" />
                  <span>{action.label}</span>
                  {action.active && (
                    <span
                      className="workflow-launcher__selected-badge"
                      aria-label="Selected"
                    >
                      <Check size={13} strokeWidth={3} aria-hidden="true" />
                    </span>
                  )}
                </button>
              ))}
            </div>
          </section>

          <section className="workflow-launcher__setup">
            <div className="workflow-launcher__setup-title">
              {getPricingTierLabel(draft.client_pricing_tier)} estimate setup
            </div>
            <div className="workflow-launcher__fields">
              <FormInput
                label="Job Name & #"
                field="job_name"
                value={draft.job_name}
                dispatch={({ field, value }) => setDraftField(field, value)}
                required={true}
              />
              <FormInput
                label="Customer Name"
                field="customer_name"
                value={draft.customer_name}
                dispatch={({ field, value }) => setDraftField(field, value)}
              />
            </div>
          </section>

          {error && <p className="workflow-launcher__error">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Later
          </Button>
          <Button onClick={handleCreate} disabled={!canCreate}>
            {isSaving ? "Creating..." : "Create estimate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WorkflowLauncher;
